/**
 * Host editor for payment links + product upsells.
 * Lives inside Settings > Client Experience > Payments & Upsells.
 * No integrations - hosts paste their own external URLs (Venmo, Zelle, Cash App, Apple Pay, PayPal).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, ShoppingBag, Plus, Trash2, ExternalLink, Save } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ui/image-upload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = [
  { value: "venmo", label: "Venmo" },
  { value: "zelle", label: "Zelle" },
  { value: "cashapp", label: "Cash App" },
  { value: "applepay", label: "Apple Pay" },
  { value: "paypal", label: "PayPal" },
  { value: "link", label: "Other" },
];

const STAGE_OPTIONS = [
  { value: "pre", label: "Pre (before appointment)" },
  { value: "during", label: "During" },
  { value: "post", label: "Post (after)" },
  { value: "all", label: "All stages" },
];

export default function PaymentsUpsellsPanel({ eventId }) {
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [productUpsells, setProductUpsells] = useState([]);
  const [paymentNote, setPaymentNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/host/${eventId}/payments-upsells`);
        if (r.ok && !cancel) {
          const d = await r.json();
          setPaymentLinks(d.payment_links || []);
          setProductUpsells(d.product_upsells || []);
          setPaymentNote(d.payment_note || "");
        }
      } catch {} finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [eventId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/host/${eventId}/payments-upsells`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_links: paymentLinks, product_upsells: productUpsells, payment_note: paymentNote }),
      });
      if (r.ok) toast.success("Saved");
      else toast.error("Save failed");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const addPaymentLink = () => setPaymentLinks([...paymentLinks, { label: "", url: "", icon: "venmo", note: "" }]);
  const removePaymentLink = (i) => setPaymentLinks(paymentLinks.filter((_, idx) => idx !== i));
  const updatePaymentLink = (i, k, v) => setPaymentLinks(paymentLinks.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const addProduct = () => setProductUpsells([...productUpsells, { name: "", photo_url: "", price_display: "", link_url: "", note: "", stage: "post" }]);
  const removeProduct = (i) => setProductUpsells(productUpsells.filter((_, idx) => idx !== i));
  const updateProduct = (i, k, v) => setProductUpsells(productUpsells.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  if (loading) return <div className="text-sm text-gray-500 p-4">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="payments-upsells-panel">
      {/* PAYMENT LINKS CARD */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Payment Links</CardTitle>
          <CardDescription>
            Add Venmo, Zelle, Cash App, or any payment URL. Clients see these on their confirmation page after the appointment. No integration - just direct links.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">General payment note (shown above your links)</Label>
            <Textarea
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="e.g. Tipping appreciated, exact change preferred, etc."
              maxLength={500}
              rows={2}
              data-testid="payment-note-input"
            />
          </div>
          {paymentLinks.map((pl, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`payment-link-row-${i}`}>
              <div className="flex gap-2">
                <select
                  value={pl.icon}
                  onChange={(e) => updatePaymentLink(i, "icon", e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1"
                  data-testid={`payment-link-icon-${i}`}
                >
                  {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Input
                  value={pl.label}
                  onChange={(e) => updatePaymentLink(i, "label", e.target.value)}
                  placeholder="Display label (e.g. Venmo)"
                  className="flex-1"
                  data-testid={`payment-link-label-${i}`}
                />
                <Button variant="ghost" size="sm" onClick={() => removePaymentLink(i)} data-testid={`payment-link-remove-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <Input
                value={pl.url}
                onChange={(e) => updatePaymentLink(i, "url", e.target.value)}
                placeholder="https://venmo.com/u/your-handle"
                data-testid={`payment-link-url-${i}`}
              />
              <Input
                value={pl.note || ""}
                onChange={(e) => updatePaymentLink(i, "note", e.target.value)}
                placeholder="Note (optional, e.g. Preferred)"
                data-testid={`payment-link-note-${i}`}
              />
            </div>
          ))}
          <Button variant="outline" onClick={addPaymentLink} className="w-full" data-testid="add-payment-link-btn">
            <Plus className="w-4 h-4 mr-1" /> Add payment link
          </Button>
        </CardContent>
      </Card>

      {/* PRODUCT UPSELLS CARD */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Product Upsells</CardTitle>
          <CardDescription>
            Recommend products clients can buy directly from your favorite retailer. Each shows as a card on the confirmation page at the chosen stage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {productUpsells.map((p, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`product-upsell-row-${i}`}>
              <div className="flex gap-2">
                <Input
                  value={p.name}
                  onChange={(e) => updateProduct(i, "name", e.target.value)}
                  placeholder="Product name"
                  className="flex-1"
                  data-testid={`product-name-${i}`}
                />
                <Button variant="ghost" size="sm" onClick={() => removeProduct(i)} data-testid={`product-remove-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <ImageUpload
                    value={p.photo_url || ""}
                    onChange={(url) => updateProduct(i, "photo_url", url)}
                    previewSize="medium"
                    aspectRatio="square"
                  />
                </div>
                <Input
                  value={p.price_display || ""}
                  onChange={(e) => updateProduct(i, "price_display", e.target.value)}
                  placeholder="Price display (e.g. $33)"
                  data-testid={`product-price-${i}`}
                />
              </div>
              <Input
                value={p.link_url || ""}
                onChange={(e) => updateProduct(i, "link_url", e.target.value)}
                placeholder="Shop link (https://...)"
                data-testid={`product-link-${i}`}
              />
              <div className="flex gap-2">
                <select
                  value={p.stage || "post"}
                  onChange={(e) => updateProduct(i, "stage", e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1"
                  data-testid={`product-stage-${i}`}
                >
                  {STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Input
                  value={p.note || ""}
                  onChange={(e) => updateProduct(i, "note", e.target.value)}
                  placeholder="Note (e.g. What I used today)"
                  className="flex-1"
                  data-testid={`product-note-${i}`}
                />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addProduct} className="w-full" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1" /> Add product
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-payments-upsells-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
